import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../../shared/lib/supabase'

const EVENT_TYPES = [
  'analyst_call', 'specialist_call', 'qa_call',
  'pipeline_failed',
  'finding_modified', 'finding_promoted', 'finding_rejected', 'finding_suppressed',
  'review_verified', 'review_delivered', 'chat_message'
]

const STATUS_FILTER = [
  { id: 'any', label: 'Any status' },
  { id: 'success', label: 'Success (2xx or no code)' },
  { id: 'client_error', label: 'Client error (4xx)' },
  { id: 'server_error', label: 'Server error (5xx)' }
]

function fmtDate(ts) {
  if (!ts) return '—'
  const d = new Date(ts)
  return d.toISOString().replace('T', ' ').slice(0, 19) + 'Z'
}

function isFailure(row) { return row.event_type === 'pipeline_failed' }
function isRetry(row) { return !!row.parent_correlation_id }

export default function InsuranceReviewAudit() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [days, setDays] = useState(7)
  const [eventTypes, setEventTypes] = useState([])
  const [statusFilter, setStatusFilter] = useState('any')
  const [codePrefix, setCodePrefix] = useState('')
  const [orgFilter, setOrgFilter] = useState('')
  const [expandedId, setExpandedId] = useState(null)
  const [orgs, setOrgs] = useState([])

  useEffect(() => {
    supabase.from('organizations').select('id, name').order('name').then(({ data }) => setOrgs(data || []))
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true); setErr(null)
    ;(async () => {
      const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()
      let q = supabase
        .from('client_insurance_review_audit')
        .select(`
          id, review_id, client_id, event_type, actor, model,
          duration_ms, notes, error_details,
          correlation_id, parent_correlation_id, created_at,
          clients (name, org_id, organizations (name))
        `)
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(500)
      if (eventTypes.length > 0) q = q.in('event_type', eventTypes)
      const { data, error } = await q
      if (cancelled) return
      if (error) { setErr(error.message); setLoading(false); return }
      let filtered = data || []
      if (codePrefix) filtered = filtered.filter(r => (r.error_details?.internal_code || '').startsWith(codePrefix))
      if (statusFilter === 'success') filtered = filtered.filter(r => !r.error_details?.http_status || (r.error_details.http_status >= 200 && r.error_details.http_status < 300))
      if (statusFilter === 'client_error') filtered = filtered.filter(r => r.error_details?.http_status >= 400 && r.error_details?.http_status < 500)
      if (statusFilter === 'server_error') filtered = filtered.filter(r => r.error_details?.http_status >= 500)
      if (orgFilter) filtered = filtered.filter(r => r.clients?.org_id === orgFilter)
      setRows(filtered)
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [days, eventTypes, statusFilter, codePrefix, orgFilter])

  const rowColor = (r) => isFailure(r) ? '#FEE2E2' : isRetry(r) ? '#FFF7ED' : 'transparent'

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Insurance Review Audit</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Cross-org pipeline audit trail. Read-only.</p>
      </div>

      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <label className="text-xs text-gray-500 dark:text-gray-400 flex flex-col">
          Days back
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value))} className="mt-1 px-2 py-1 border rounded">
            <option value={1}>1</option>
            <option value={7}>7</option>
            <option value={30}>30</option>
            <option value={90}>90</option>
          </select>
        </label>
        <label className="text-xs text-gray-500 dark:text-gray-400 flex flex-col">
          Status
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="mt-1 px-2 py-1 border rounded">
            {STATUS_FILTER.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>
        <label className="text-xs text-gray-500 dark:text-gray-400 flex flex-col">
          Internal code prefix
          <input value={codePrefix} onChange={(e) => setCodePrefix(e.target.value)} placeholder="IR-PIPE-429" className="mt-1 px-2 py-1 border rounded" />
        </label>
        <label className="text-xs text-gray-500 dark:text-gray-400 flex flex-col">
          Org
          <select value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)} className="mt-1 px-2 py-1 border rounded">
            <option value="">All orgs</option>
            {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
      </div>

      {loading ? <p>Loading…</p> : err ? <p className="text-red-600">Error: {err}</p> : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b">
              <th className="py-2 pr-2">Time</th>
              <th className="py-2 pr-2">Client</th>
              <th className="py-2 pr-2">Org</th>
              <th className="py-2 pr-2">Event</th>
              <th className="py-2 pr-2">Stage</th>
              <th className="py-2 pr-2">HTTP</th>
              <th className="py-2 pr-2">Internal Code</th>
              <th className="py-2 pr-2">Duration</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tbody key={r.id} style={{ backgroundColor: rowColor(r) }} className="border-b">
                <tr onClick={() => setExpandedId(expandedId === r.id ? null : r.id)} className="cursor-pointer">
                  <td className="py-2 pr-2 font-mono text-xs">{fmtDate(r.created_at)}</td>
                  <td className="py-2 pr-2">{r.clients?.name || r.client_id}</td>
                  <td className="py-2 pr-2">{r.clients?.organizations?.name || '—'}</td>
                  <td className="py-2 pr-2">{r.event_type}</td>
                  <td className="py-2 pr-2">{r.error_details?.stage || '—'}</td>
                  <td className="py-2 pr-2 font-mono text-xs">{r.error_details?.http_status || '—'}</td>
                  <td className="py-2 pr-2 font-mono text-xs">{r.error_details?.internal_code || '—'}</td>
                  <td className="py-2 pr-2">{r.duration_ms ? `${(r.duration_ms/1000).toFixed(1)}s` : '—'}</td>
                </tr>
                {expandedId === r.id && (
                  <tr>
                    <td colSpan={8} className="py-3 px-2 bg-gray-50 dark:bg-gray-900">
                      <div className="text-xs">
                        <div><strong>Correlation ID:</strong> <code>{r.correlation_id || '—'}</code></div>
                        <div><strong>Parent correlation:</strong> <code>{r.parent_correlation_id || '—'}</code></div>
                        <div><strong>Review ID:</strong> <code>{r.review_id}</code></div>
                        <div><strong>Notes:</strong> {r.notes || '—'}</div>
                        <div className="mt-2"><strong>Error details:</strong></div>
                        <pre className="mt-1 p-2 bg-white dark:bg-gray-800 border rounded overflow-x-auto">{JSON.stringify(r.error_details || {}, null, 2)}</pre>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
